/**
 * Commodity Cancel Dialog (KAN-307)
 *
 * Simple cancellation confirmation with:
 * - Trade summary
 * - Permanent warning
 * - Allocation children warning
 * - Required cancellation reason
 * - Confirmation checkbox
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';
import { commodityModificationService } from '../../../services/api/commodityModificationService';

interface Props {
  trade: any;
  clientId: string;
  canSelfApprove: boolean;
  onSuccess: (result: any) => void;
  onClose: () => void;
}

const CommodityCancelDialog: React.FC<Props> = ({ trade, clientId, canSelfApprove, onSuccess, onClose }) => {
  const { t, i18n } = useTranslation();

  const [cancellationReason, setCancellationReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entityId = trade.entityId || clientId;

  const formatPrice = (value: any) => {
    if (value == null) return '—';
    return Number(value).toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };

  const translatedProduct = t(`commodities.enums.${(trade.ProductType || '').toLowerCase()}`, trade.ProductType || '');
  const translatedDirection = t(`commodities.enums.${(trade.Direction || '').toLowerCase()}`, trade.Direction || '');
  const lotsUnit = t('commodities.modifications.lotsUnit');
  const tradeSummary = `${trade.TradeNumber} — ${translatedProduct} ${translatedDirection} ${trade.UnderlyingName || ''} (${trade.Lots} ${lotsUnit} @ ${formatPrice(trade.Price)} ${trade.UnitOfMeasure || ''})`;

  const handleSubmit = useCallback(async () => {
    if (!cancellationReason.trim()) {
      setError(t('commodities.modifications.cancellationReasonRequired'));
      return;
    }
    if (canSelfApprove && !confirmed) return;

    setSubmitting(true);
    setError(null);

    try {
      if (canSelfApprove) {
        // Single-step: cancel directly
        const res = await commodityModificationService.cancelDirect(
          clientId,
          trade.id,
          {
            cancellationReason: cancellationReason.trim(),
            comment: cancellationReason.trim(),
          },
          entityId
        );
        if (res.success) {
          onSuccess(res.data);
        } else {
          setError(res.message || t('commodities.modifications.cancelFailed'));
        }
      } else {
        // Two-step: submit for approval
        const res = await commodityModificationService.initiate(
          clientId,
          trade.id,
          { action: 'cancel', comment: cancellationReason.trim() },
          entityId
        );
        if (res.success) {
          onSuccess(res.data);
        } else {
          setError(res.message || t('commodities.modifications.cancelFailed'));
        }
      }
    } catch (err: any) {
      setError(err.message || t('commodities.modifications.cancelFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [cancellationReason, confirmed, clientId, trade.id, entityId, onSuccess, t, canSelfApprove]);

  return (
    <div className="commodity-mod-dialog-overlay" onClick={onClose}>
      <div className="commodity-mod-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="commodity-mod-dialog-header">
          <h2>{canSelfApprove ? t('commodities.modifications.cancelTitle') : t('commodities.modifications.initiateTitle')}</h2>
          <button className="commodity-mod-dialog-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="commodity-mod-dialog-body">
          {/* Trade summary */}
          <div className="commodity-mod-trade-summary">
            <div className="trade-title">{tradeSummary}</div>
            <div className="trade-detail">
              {t('commodities.positions.grid.counterpartyName')}: {trade.CounterpartyName || '—'}
            </div>
          </div>

          {/* Warning: permanent for self-approve, request info for non-self-approve */}
          {canSelfApprove ? (
            <div className="commodity-mod-warning danger">
              <AlertTriangle size={16} />
              {t('commodities.modifications.permanentWarning')}
            </div>
          ) : (
            <div className="commodity-mod-warning">
              <AlertTriangle size={16} />
              {t('commodities.modifications.initiateWarning')}
            </div>
          )}

          {/* Cancellation reason / comment */}
          <div className="commodity-mod-comment">
            <label>{canSelfApprove ? t('commodities.modifications.cancellationReasonLabel') : t('commodities.modifications.commentLabel')} *</label>
            <textarea
              value={cancellationReason}
              onChange={(e) => { setCancellationReason(e.target.value); setError(null); }}
              placeholder={canSelfApprove ? t('commodities.modifications.cancellationReasonPlaceholder') : t('commodities.modifications.commentPlaceholder')}
              rows={3}
            />
            {error && <div className="field-error">{error}</div>}
          </div>

          {/* Confirmation checkbox (only for self-approve — they are actually cancelling) */}
          {canSelfApprove && (
            <label className="commodity-mod-checkbox">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              {t('commodities.modifications.confirmCheckbox')}
            </label>
          )}
        </div>

        {/* Footer */}
        <div className="commodity-mod-dialog-footer">
          <button className="btn-secondary" onClick={onClose} disabled={submitting}>
            {t('commodities.modifications.goBack')}
          </button>
          <button
            className={canSelfApprove ? 'btn-danger' : 'btn-primary'}
            onClick={handleSubmit}
            disabled={submitting || !cancellationReason.trim() || (canSelfApprove && !confirmed)}
          >
            {submitting
              ? t('commodities.modifications.submitting')
              : canSelfApprove
                ? t('commodities.modifications.confirmCancellation')
                : t('commodities.modifications.submitRequest')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommodityCancelDialog;
